import './WidgetStyle.css'; 
import './ResponsiveFonts.css';
import { useFont } from '../../Design/FontContext';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import DeleteModal from './DeleteModal';
import { getTextEffectStyles } from '../utils/textEffectsUtils';
import '../WidgetEditComponents/TextEffectsAnimations.css';
import './MobileBreakpoints.css';
import { generateLayoutStyles, getResponsiveValue, appendPx } from '../WidgetEditComponents/LayoutUtils';
import AIRefreshIcon from '../../components/AIRefreshIcon';

function SpiritualFeatureBanner({ data, onRemove, isSelected, isEditMode, onUpdate, onSelectWidget }) {
    const deleteLogo = '/img/delete.svg';
    const fonts = useFont();
    
    // Mobile Layout State und Container Ref (ZUERST definieren!)
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [isMobileLayout, setIsMobileLayout] = useState(false);
    const containerRef = useRef(null);
    
    // INLINE-EDITING SYSTEM (nach README Pattern)
    const [isInlineEditing, setIsInlineEditing] = useState(null);
    const [inlineValue, setInlineValue] = useState('');
    const [originalLayout, setOriginalLayout] = useState({});
    
    // Animation Trigger State (wie BragBar)
    const [animationKey, setAnimationKey] = useState(0);
    
    // Ref für Outside-Click Detection
    const widgetRef = useRef(null);

    // ✅ MOBILE BREAKPOINT DETECTION (750px Standard - NEW LAYOUT SYSTEM 2025)
    useEffect(() => {
        const checkContainerWidth = () => {
            if (containerRef.current) {
                const containerWidth = containerRef.current.offsetWidth;
                setIsMobileLayout(containerWidth < 750); // ✅ Standardized Breakpoint
            }
        };

        checkContainerWidth();
        window.addEventListener('resize', checkContainerWidth);
        
        const observer = new ResizeObserver(checkContainerWidth);
        if (containerRef.current) {
            observer.observe(containerRef.current);
        }

        return () => {
            window.removeEventListener('resize', checkContainerWidth);
            observer.disconnect();
        };
    }, []);

    // Animation Trigger Effect (wie BragBar)
    useEffect(() => {
        if (data.triggerAnimation && isEditMode) {
            setAnimationKey(prev => prev + 1);
        }
    }, [data.triggerAnimation, isEditMode]);

    // Delete Handler
    const handleDelete = (event) => {
        event.stopPropagation();
        if (onRemove) {
            onRemove({ stopPropagation: () => {} }, data.id);
        }
        setShowDeleteModal(false);
    };

    // INLINE-EDITING FUNCTIONS (nach README Pattern)
    const handleInlineEdit = (fieldName, currentValue) => {
        if (!isEditMode || !onUpdate) return;
        
        // Widget selektieren wenn nicht bereits selektiert
        if (!isSelected && onSelectWidget) {
            onSelectWidget(data.id);
        }
        
        if (isInlineEditing === fieldName) {
            handleInlineCancel();
            return;
        }
        setIsInlineEditing(fieldName);
        setInlineValue(currentValue || '');
    };

    const handleInlineSubmit = useCallback((fieldName) => {
        if (!onUpdate) {
            console.warn('⚠️ onUpdate not available');
            return;
        }
        
        console.log('💾 SpiritualFeatureBanner Saving field:', fieldName, 'with value:', inlineValue);
        
        // Handle nested feature updates
        if (fieldName.includes('.')) {
            const [featureIndex, featureField] = fieldName.split('.');
            const currentFeatures = data.features || [];
            const updatedFeatures = [...currentFeatures];
            updatedFeatures[parseInt(featureIndex)] = {
                ...updatedFeatures[parseInt(featureIndex)],
                [featureField]: inlineValue
            };
            const updatedData = { ...data, features: updatedFeatures };
            onUpdate(updatedData);
        } else {
            const updatedData = { ...data, [fieldName]: inlineValue };
            onUpdate(updatedData);
        }
        
        setIsInlineEditing(null);
        setInlineValue('');
        console.log('✅ SpiritualFeatureBanner Save completed for:', fieldName);
    }, [onUpdate, data, inlineValue]);

    const handleInlineCancel = () => {
        setIsInlineEditing(null);
        setInlineValue('');
    };

    const handleInlineKeyPress = (e, fieldName) => {
        const isCurrentlyTextarea = e.target.tagName === 'TEXTAREA';
        
        if (e.key === 'Enter') {
            if (isCurrentlyTextarea) {
                if (e.ctrlKey || e.metaKey) {
                    e.preventDefault();
                    handleInlineSubmit(fieldName);
                }
            } else {
                e.preventDefault();
                handleInlineSubmit(fieldName);
            }
        } else if (e.key === 'Escape') {
            handleInlineCancel();
        }
    };

    // Smart Outside-Click Handler - Nach README Pattern
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (!isInlineEditing) return;
            
            const isEditingInput = event.target.tagName === 'TEXTAREA' || event.target.tagName === 'INPUT';
            const isActionButton = event.target.hasAttribute('data-action-button');
            const isDeleteButton = event.target.closest('.delete_button');
            const isWidgetEdit = event.target.closest('.widget-edit');
            const isAddWidgets = event.target.closest('.add-widgets');
            const isPagePreview = event.target.closest('.page_preview');
            
            const isRealOutsideClick = !isEditingInput && 
                                     !isActionButton && 
                                     !isDeleteButton && 
                                     !isWidgetEdit &&
                                     (isPagePreview || !widgetRef.current?.contains(event.target));
            
            if (isRealOutsideClick) {
                if (onUpdate && inlineValue !== undefined) {
                    // Handle nested feature updates
                    if (isInlineEditing.includes('.')) {
                        const [featureIndex, featureField] = isInlineEditing.split('.');
                        const currentFeatures = data.features || [];
                        const updatedFeatures = [...currentFeatures];
                        updatedFeatures[parseInt(featureIndex)] = {
                            ...updatedFeatures[parseInt(featureIndex)],
                            [featureField]: inlineValue
                        };
                        const updatedData = { ...data, features: updatedFeatures };
                        onUpdate(updatedData);
                    } else {
                        const updatedData = { ...data, [isInlineEditing]: inlineValue };
                        onUpdate(updatedData);
                    }
                }
                setIsInlineEditing(null);
                setInlineValue('');
            }
        };

        if (isInlineEditing) {
            document.addEventListener('mousedown', handleClickOutside, true);
            return () => {
                document.removeEventListener('mousedown', handleClickOutside, true);
            };
        }
    }, [isInlineEditing, inlineValue, data, onUpdate]);

    // RENDER EDITABLE TEXT FUNCTION - Korrekt nach README Pattern
    const renderEditableText = (fieldName, currentValue, Element = 'p', style = {}, placeholder = 'Click to edit...') => {
        if (!isEditMode) {
            return currentValue ? (
                <Element style={{
                    ...style,
                    whiteSpace: style.whiteSpace || 'pre-wrap'
                }}>
                    {currentValue}
                </Element>
            ) : null;
        }

        if (isInlineEditing === fieldName) {
            const layoutData = originalLayout;
            const isTextarea = Element !== 'button';
            
            // Action Buttons Container
            const renderActionButtons = () => (
                <div style={{
                    position: 'absolute',
                    top: '-11px',
                    right: '-6px',
                    display: 'flex',
                    gap: '4px',
                    zIndex: 9999,
                    background: 'rgba(255, 255, 255, 0.95)',
                    padding: '2px 4px',
                    borderRadius: '6px',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                    backdropFilter: 'blur(4px)',
                    border: '1px solid rgba(0, 0, 0, 0.05)'
                }}>
                    <button
                        data-action-button="save"
                        onClick={() => handleInlineSubmit(fieldName)}
                        onMouseDown={(e) => e.preventDefault()}
                        style={{
                            background: '#10b981',
                            color: 'white',
                            border: 'none',
                            borderRadius: '50%',
                            padding: '0',
                            fontSize: '9px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '16px',
                            height: '16px',
                            transition: 'all 0.2s ease',
                            boxShadow: '0 1px 3px rgba(16, 185, 129, 0.3)'
                        }}
                        title="Save"
                    >
                        ✓
                    </button>
                    
                    <button
                        data-action-button="cancel"
                        onClick={handleInlineCancel}
                        onMouseDown={(e) => e.preventDefault()}
                        style={{
                            background: '#ef4444',
                            color: 'white',
                            border: 'none',
                            borderRadius: '50%',
                            padding: '0',
                            fontSize: '9px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '16px',
                            height: '16px',
                            transition: 'all 0.2s ease',
                            boxShadow: '0 1px 3px rgba(239, 68, 68, 0.3)'
                        }}
                        title="Cancel"
                    >
                        ×
                    </button>
                </div>
            );
            
            return isTextarea ? (
                <div style={{ 
                    position: 'relative',
                    width: '100%',
                    marginTop: layoutData.marginTop || '0',
                    marginBottom: layoutData.marginBottom || '0',
                    marginLeft: layoutData.marginLeft || '0',
                    marginRight: layoutData.marginRight || '0'
                }}>
                    <textarea
                        value={inlineValue}
                        onChange={(e) => {
                            setInlineValue(e.target.value);
                            const target = e.target;
                            const cursorPosition = target.selectionStart;
                            setTimeout(() => {
                                target.style.height = 'auto';
                                target.style.height = target.scrollHeight + 'px';
                                target.setSelectionRange(cursorPosition, cursorPosition);
                            }, 0);
                        }}
                        onKeyDown={(e) => {
                            handleInlineKeyPress(e, fieldName);
                            if (e.key === 'Enter' && !e.ctrlKey && !e.metaKey) {
                                setTimeout(() => {
                                    const target = e.target;
                                    target.style.height = 'auto';
                                    target.style.height = target.scrollHeight + 'px';
                                }, 0);
                            }
                        }}
                        onBlur={() => handleInlineSubmit(fieldName)}
                        placeholder={placeholder}
                        autoFocus
                        onInput={(e) => {
                            e.target.style.height = 'auto';
                            const originalHeight = parseInt(layoutData.height) || parseInt(style.fontSize) * 1.6;
                            e.target.style.height = Math.max(e.target.scrollHeight, originalHeight) + 'px';
                        }}
                        ref={(textarea) => {
                            if (textarea) {
                                setTimeout(() => {
                                    const originalHeight = parseInt(layoutData.height) || parseInt(style.fontSize) * 1.6;
                                    textarea.style.height = 'auto';
                                    textarea.style.height = Math.max(textarea.scrollHeight, originalHeight) + 'px';
                                    if (textarea.selectionStart === 0 && textarea.selectionEnd === 0) {
                                        textarea.setSelectionRange(textarea.value.length, textarea.value.length);
                                    }
                                }, 0);
                            }
                        }}
                        style={{
                            ...style,
                            // LEAN Textarea-Styling (nach README)
                            background: 'transparent',
                            border: 'none',
                            outline: 'none',
                            resize: 'none',
                            padding: '4px 8px',
                            margin: '0',
                            width: '100%',
                            minHeight: layoutData.height || 'auto',
                            // GRADIENT-FIX: Grauer Text während Bearbeitung
                            color: '#333333',
                            backgroundImage: 'none',
                            backgroundClip: 'unset',
                            webkitBackgroundClip: 'unset',
                            webkitTextFillColor: '#333333',
                            // Original-Text-Eigenschaften beibehalten
                            fontFamily: style.fontFamily,
                            fontSize: style.fontSize,
                            fontWeight: style.fontWeight,
                            lineHeight: style.lineHeight,
                            letterSpacing: style.letterSpacing,
                            textAlign: style.textAlign || 'inherit',
                            whiteSpace: style.whiteSpace || 'pre-wrap'
                        }}
                    />
                    {renderActionButtons()}
                </div>
            ) : (
                // BUTTONS: Input-Felder (nach README)
                <input
                    type="text"
                    value={inlineValue}
                    onChange={(e) => setInlineValue(e.target.value)}
                    onKeyDown={(e) => handleInlineKeyPress(e, fieldName)}
                    onBlur={() => handleInlineSubmit(fieldName)}
                    placeholder={placeholder}
                    autoFocus
                    ref={(input) => {
                        if (input) {
                            setTimeout(() => {
                                if (input.selectionStart === 0 && input.selectionEnd === 0) {
                                    input.setSelectionRange(input.value.length, input.value.length);
                                }
                            }, 0);
                        }
                    }}
                    style={{
                        ...style,
                        width: layoutData.width || style.width,
                        height: layoutData.height || style.height,
                        backgroundColor: style.backgroundColor,
                        border: style.border || 'none',
                        borderRadius: style.borderRadius,
                        padding: style.padding,
                        boxShadow: 'none',
                        cursor: 'text',
                        textAlign: 'center',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                    }}
                />
            );
        }

        // Edit-Modus: Inline-Editing ermöglichen
        const handleElementClick = (e) => {
            e.preventDefault();
            e.stopPropagation();
            
            const rect = e.target.getBoundingClientRect();
            const computedStyle = window.getComputedStyle(e.target);
            
            // LEAN Layout-Erfassung (nach README)
            const layoutData = {
                width: rect.width + 'px',
                height: rect.height + 'px',
                marginTop: computedStyle.marginTop,
                marginBottom: computedStyle.marginBottom,
                marginLeft: computedStyle.marginLeft,
                marginRight: computedStyle.marginRight
            };
            
            setOriginalLayout(layoutData);
            handleInlineEdit(fieldName, currentValue);
        };

        return (
            <Element 
                style={{
                    ...style,
                    cursor: Element === 'button' ? 'pointer' : 'text',
                    position: 'relative',
                    transition: 'all 0.2s ease',
                    padding: Element === 'button' ? style.padding : '4px 8px',
                    borderRadius: style.borderRadius || (Element === 'button' ? style.borderRadius : '4px'),
                    border: Element === 'button' ? style.border : 'none',
                    boxShadow: Element === 'button' ? style.boxShadow : 'none'
                }}
                onClick={handleElementClick}
                onMouseEnter={(e) => {
                    if (Element !== 'button') {
                        e.target.style.cursor = 'text';
                    } else {
                        e.target.style.cursor = 'pointer';
                    }
                }}
                onMouseLeave={(e) => {
                    e.target.style.cursor = Element === 'button' ? 'pointer' : 'text';
                }}
                title="Click to edit"
            >
                {currentValue || placeholder}
                {/* Blinkender Cursor beim Editieren */}
                {isEditMode && isInlineEditing === fieldName && (
                    <span style={{
                        display: 'inline',
                        animation: 'blink 1s infinite',
                        fontSize: 'inherit',
                        color: 'inherit',
                        marginLeft: '1px'
                    }}>
                        |
                    </span>
                )}
            </Element>
        );
    };

    // Widget Styling
    function getBorderStyle() {
        if (isEditMode) {
            if (isSelected) {
                return '2px dashed #000000';
            } else {
                return '2px dashed transparent';
            }
        } else {
            return 'none';
        }
    }

    // 🚀 REVOLUTIONARY LAYOUT SYSTEM - Responsive Styles generieren
    const layoutStyles = generateLayoutStyles(data, isMobileLayout, {
        backgroundColor: data.backgroundColor || '#f9f8f6',
        border: getBorderStyle(),
        position: 'relative',
        overflow: 'hidden',
        width: '100%',
        maxWidth: 'none',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    });

    const widgetStyle = {
        ...layoutStyles,
        // Fallback für alte Height-Property (Backwards Compatibility)
        ...(layoutStyles.height === 'auto' && !layoutStyles.minHeight && {
            minHeight: appendPx(data.height || 600)
        })
    };

    return (
        <>
            {/* CSS Animations */}
            <style>
                {`
                    @keyframes blink {
                        0%, 50% { opacity: 1; }
                        51%, 100% { opacity: 0; }
                    }
                    
                    /* Animation Keyframes für Preview */
                    @keyframes fadeInUp {
                        from {
                            opacity: 0;
                            transform: translateY(30px);
                        }
                        to {
                            opacity: 1;
                            transform: translateY(0);
                        }
                    }
                    
                    @keyframes slideIn {
                        from {
                            opacity: 0;
                            transform: translateX(-30px);
                        }
                        to {
                            opacity: 1;
                            transform: translateX(0);
                        }
                    }
                    
                    @keyframes fadeIn {
                        from {
                            opacity: 0;
                        }
                        to {
                            opacity: 1;
                        }
                    }
                    
                    @keyframes fadeInSlow {
                        0% {
                            opacity: 0;
                        }
                        30% {
                            opacity: 0;
                        }
                        100% {
                            opacity: 1;
                        }
                    }
                    
                    /* Animation Classes für Features */
                    .spiritual-feature-animated {
                        animation-fill-mode: both;
                    }
                    
                    .spiritual-feature-animated.fadeInUp {
                        animation-name: fadeInUp;
                    }
                    
                    .spiritual-feature-animated.slideIn {
                        animation-name: slideIn;
                    }
                    
                    .spiritual-feature-animated.fadeIn {
                        animation-name: fadeIn;
                    }
                    
                    .spiritual-feature-animated.fadeInSlow {
                        animation-name: fadeInSlow;
                    }
                `}
            </style>
            
            <div 
                ref={(el) => {
                    containerRef.current = el;
                    widgetRef.current = el;
                }}
                style={widgetStyle}
                className={`widget-container spiritual-feature-banner-widget ${isSelected ? 'selected' : ''}`}
            >
                {/* Delete Button */}
                {isEditMode && (
                    <button className="delete_button" onClick={() => setShowDeleteModal(true)}>
                        <img className="delete_logo" src={deleteLogo} alt="Löschen" />
                    </button>
                )}
                
                {/* 🤖 AI REFRESH ICON */}
                {isEditMode && (
                    <AIRefreshIcon 
                        widget={{
                            ...data,
                            type: 'SpiritualFeatureBanner'
                        }}
                        spaceId={null}
                        onContentRefreshed={(refreshedWidget) => {
                            console.log('🤖 [SPIRITUALFEATUREBANNER] AI-refreshed content received:', refreshedWidget);
                            onUpdate(refreshedWidget);
                        }}
                        position="top-left"
                    />
                )}
                {isEditMode && showDeleteModal && (
                    <DeleteModal
                        isOpen={showDeleteModal}
                        onClose={() => setShowDeleteModal(false)}
                        onConfirm={(event) => handleDelete(event, data.id)}
                    />
                )}

                {/* Main Container - Layout-spezifische Struktur */}
                <div style={{
                    padding: `${getResponsiveValue(data, 'paddingTop', isMobileLayout, isMobileLayout ? 40 : 80)}px ${data.sideSpacing || 60}px ${getResponsiveValue(data, 'paddingBottom', isMobileLayout, isMobileLayout ? 40 : 80)}px ${data.sideSpacing || 60}px`,
                    maxWidth: `${data.contentMaxWidth || 1200}px`,
                    width: '100%'
                }} className="spiritual-feature-banner-container">
                    
                    {/* CLASSIC CENTER LAYOUT - Bild zentriert, Features links/rechts */}
                    {((data.layoutType === 'classic-center' || data.layoutStyle === 'classic-center') || (!data.layoutType && !data.layoutStyle)) && (
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: isMobileLayout ? `${data.featureVerticalSpacing || 60}px` : `${data.featureSpacing || 40}px`, // ✅ Mobile: featureVerticalSpacing, Desktop: featureSpacing
                            flexDirection: isMobileLayout ? 'column' : 'row',
                            minHeight: '500px'
                        }}>
                            {/* Left Features (Features 1-2) - Nur Desktop */}
                            {!isMobileLayout && (
                                <div style={{
                                    flex: '1',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: `${data.featureVerticalSpacing || 60}px`,
                                    alignItems: 'flex-end',
                                    paddingRight: '20px'
                                }}>
                                    {data.features?.slice(0, 2).map((feature, index) => {
                                        // ✅ CHECK INDIVIDUAL FEATURE VISIBILITY
                                        const isFeatureVisible = data[`showFeature${index + 1}`] !== false;
                                        
                                        if (!isFeatureVisible) return null;
                                        
                                        return (
                                        <div 
                                            key={`${feature.id}-${animationKey}`} 
                                            className={data.enableAnimations && isEditMode ? `spiritual-feature-animated ${data.animationType || 'fadeInUp'}` : ''}
                                            style={{
                                                maxWidth: '300px',
                                                animationDuration: data.enableAnimations && isEditMode ? `${data.animationDuration || 600}ms` : '0s',
                                                animationDelay: data.enableAnimations && isEditMode ? `${index * (data.animationDelay || 100)}ms` : '0s'
                                            }}
                                        >
                                            {/* ✅ MINI TITLE - oben über dem Haupttitel */}
                                            {feature.showMiniTitle !== false && feature.miniTitle && (
                                                renderEditableText(
                                                    `${index}.miniTitle`, 
                                                    feature.miniTitle, 
                                                    'div', 
                                                    {
                                                        color: data.featureDescriptionColor || '#5a5a5a', // ✅ Übernimmt Description-Farbe
                                                        fontSize: appendPx(data.featureMiniTitleSize || 12),
                                                        fontWeight: data.featureMiniTitleWeight || '500',
                                                        fontFamily: data.featureMiniTitleFont || fonts.globalSmallFont,
                                                        lineHeight: '1.4',
                                                        textAlign: 'right',
                                                        margin: '0 0 6px 0',
                                                        textTransform: 'uppercase',
                                                        letterSpacing: '0.5px'
                                                    },
                                                    'Enter mini title...'
                                                )
                                            )}

                                            {renderEditableText(
                                                `${index}.title`, 
                                                feature.title, 
                                                'h3', 
                                                {
                                                    color: data.featureTitleColor || '#2c3e50',
                                                    fontSize: appendPx(data.featureTitleSize || 28),
                                                    fontWeight: data.featureTitleWeight || '700',
                                                    fontFamily: data.featureTitleFont || fonts.globalBigFont,
                                                    lineHeight: '1.3',
                                                    textAlign: 'right',
                                                    margin: '0 0 12px 0'
                                                },
                                                'Enter feature title...'
                                            )}

                                            {/* ✅ TITLE SEPARATOR - dünne Linie unter dem Titel */}
                                            {data.showFeatureTitleSeparator && (
                                                <div style={{
                                                    display: 'flex',
                                                    justifyContent: 'flex-end',
                                                    marginBottom: appendPx(data.featureTitleSeparatorSpacing || 8)
                                                }}>
                                                    <div style={{
                                                        width: `${data.featureTitleSeparatorWidth || 30}%`,
                                                        height: appendPx(data.featureTitleSeparatorThickness || 2),
                                                        backgroundColor: data.featureTitleSeparatorColor || data.featureTitleColor || '#5C80BC',
                                                        borderRadius: appendPx((data.featureTitleSeparatorThickness || 2) / 2)
                                                    }} />
                                                </div>
                                            )}
                                            
                                            {renderEditableText(
                                                `${index}.description`, 
                                                feature.description, 
                                                'p', 
                                                {
                                                    color: data.featureDescriptionColor || '#5a5a5a',
                                                    fontSize: appendPx(data.featureDescriptionSize || 16),
                                                    fontWeight: data.featureDescriptionWeight || '400',
                                                    fontFamily: data.featureDescriptionFont || fonts.globalSmallFont,
                                                    lineHeight: '1.6',
                                                    textAlign: 'right',
                                                    margin: '0 0 12px 0'
                                                },
                                                'Enter feature description...'
                                            )}

                                            {/* ✅ PRICE LABEL - unten unter der Description, rechtsbündig wie Text */}
                                            {feature.showPriceLabel !== false && feature.priceLabel && (
                                                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                                    {renderEditableText(
                                                        `${index}.priceLabel`, 
                                                        feature.priceLabel, 
                                                        'div', 
                                                        {
                                                            color: data.featureTitleColor || '#2c3e50', // ✅ Übernimmt Titel-Farbe
                                                            fontSize: appendPx(data.featurePriceLabelSize || 14),
                                                            fontWeight: data.featurePriceLabelWeight || '600',
                                                            fontFamily: data.featurePriceLabelFont || fonts.globalSmallFont,
                                                            lineHeight: '1.4',
                                                            textAlign: 'right',
                                                            margin: '0',
                                                            display: 'inline-block'
                                                        },
                                                        'Enter price...'
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                        );
                                    })}
                                </div>
                            )}

                            {/* Center Image - Desktop */}
                            {!isMobileLayout && data.showCenterImage && data.centerImage && (
                                <div style={{
                                    flex: '0 0 auto',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center'
                                }}>
                                    <img
                                        src={data.centerImage}
                                        alt="Spiritual Feature"
                                        style={{
                                            width: appendPx(data.centerImageWidth || 400),
                                            height: appendPx(data.centerImageHeight || 500),
                                            objectFit: data.centerImageFit || 'cover',
                                            borderRadius: appendPx(data.centerImageBorderRadius || 12),
                                            boxShadow: data.centerImageShadow ? '0 8px 32px rgba(0, 0, 0, 0.15)' : 'none'
                                        }}
                                        className="spiritual-feature-banner-image"
                                    />
                                </div>
                            )}

                            {/* Right Features (Features 3-4) - Nur Desktop */}
                            {!isMobileLayout && (
                                <div style={{
                                    flex: '1',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: `${data.featureVerticalSpacing || 60}px`,
                                    alignItems: 'flex-start',
                                    paddingLeft: '20px'
                                }}>
                                    {data.features?.slice(2, 4).map((feature, index) => {
                                        // ✅ CHECK INDIVIDUAL FEATURE VISIBILITY (Features 3-4)
                                        const isFeatureVisible = data[`showFeature${index + 3}`] !== false;
                                        
                                        if (!isFeatureVisible) return null;
                                        
                                        return (
                                        <div 
                                            key={`${feature.id}-${animationKey}`} 
                                            className={data.enableAnimations && isEditMode ? `spiritual-feature-animated ${data.animationType || 'fadeInUp'}` : ''}
                                            style={{
                                                maxWidth: '300px',
                                                animationDuration: data.enableAnimations && isEditMode ? `${data.animationDuration || 600}ms` : '0s',
                                                animationDelay: data.enableAnimations && isEditMode ? `${(index + 2) * (data.animationDelay || 100)}ms` : '0s'
                                            }}
                                        >
                                            {/* ✅ MINI TITLE - oben über dem Haupttitel */}
                                            {feature.showMiniTitle !== false && feature.miniTitle && (
                                                renderEditableText(
                                                    `${index + 2}.miniTitle`, 
                                                    feature.miniTitle, 
                                                    'div', 
                                                    {
                                                        color: data.featureDescriptionColor || '#5a5a5a', // ✅ Übernimmt Description-Farbe
                                                        fontSize: appendPx(data.featureMiniTitleSize || 12),
                                                        fontWeight: data.featureMiniTitleWeight || '500',
                                                        fontFamily: data.featureMiniTitleFont || fonts.globalSmallFont,
                                                        lineHeight: '1.4',
                                                        textAlign: 'left',
                                                        margin: '0 0 6px 0',
                                                        textTransform: 'uppercase',
                                                        letterSpacing: '0.5px'
                                                    },
                                                    'Enter mini title...'
                                                )
                                            )}

                                            {renderEditableText(
                                                `${index + 2}.title`, 
                                                feature.title, 
                                                'h3', 
                                                {
                                                    color: data.featureTitleColor || '#2c3e50',
                                                    fontSize: appendPx(data.featureTitleSize || 28),
                                                    fontWeight: data.featureTitleWeight || '700',
                                                    fontFamily: data.featureTitleFont || fonts.globalBigFont,
                                                    lineHeight: '1.3',
                                                    textAlign: 'left',
                                                    margin: '0 0 12px 0'
                                                },
                                                'Enter feature title...'
                                            )}

                                            {/* ✅ TITLE SEPARATOR - dünne Linie unter dem Titel */}
                                            {data.showFeatureTitleSeparator && (
                                                <div style={{
                                                    display: 'flex',
                                                    justifyContent: 'flex-start',
                                                    marginBottom: appendPx(data.featureTitleSeparatorSpacing || 8)
                                                }}>
                                                    <div style={{
                                                        width: `${data.featureTitleSeparatorWidth || 30}%`,
                                                        height: appendPx(data.featureTitleSeparatorThickness || 2),
                                                        backgroundColor: data.featureTitleSeparatorColor || data.featureTitleColor || '#5C80BC',
                                                        borderRadius: appendPx((data.featureTitleSeparatorThickness || 2) / 2)
                                                    }} />
                                                </div>
                                            )}
                                            
                                            {renderEditableText(
                                                `${index + 2}.description`, 
                                                feature.description, 
                                                'p', 
                                                {
                                                    color: data.featureDescriptionColor || '#5a5a5a',
                                                    fontSize: appendPx(data.featureDescriptionSize || 16),
                                                    fontWeight: data.featureDescriptionWeight || '400',
                                                    fontFamily: data.featureDescriptionFont || fonts.globalSmallFont,
                                                    lineHeight: '1.6',
                                                    textAlign: 'left',
                                                    margin: '0 0 12px 0'
                                                },
                                                'Enter feature description...'
                                            )}

                                            {/* ✅ PRICE LABEL - unten unter der Description, linksbündig wie Text */}
                                            {feature.showPriceLabel !== false && feature.priceLabel && (
                                                <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                                                    {renderEditableText(
                                                        `${index + 2}.priceLabel`, 
                                                        feature.priceLabel, 
                                                        'div', 
                                                        {
                                                            color: data.featureTitleColor || '#2c3e50', // ✅ Übernimmt Titel-Farbe
                                                            fontSize: appendPx(data.featurePriceLabelSize || 14),
                                                            fontWeight: data.featurePriceLabelWeight || '600',
                                                            fontFamily: data.featurePriceLabelFont || fonts.globalSmallFont,
                                                            lineHeight: '1.4',
                                                            textAlign: 'left',
                                                            margin: '0',
                                                            display: 'inline-block'
                                                        },
                                                        'Enter price...'
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                        );
                                    })}
                                </div>
                            )}

                            {/* Mobile - Image + Features vertikal */}
                            {isMobileLayout && (
                                <>
                                    {/* Mobile Image */}
                                    {data.showCenterImage && data.centerImage && (
                                        <div style={{
                                            width: '100%',
                                            display: 'flex',
                                            justifyContent: 'center',
                                            marginBottom: '40px'
                                        }}>
                                            <img
                                                src={data.centerImage}
                                                alt="Spiritual Feature"
                                                style={{
                                                    width: appendPx(Math.min(data.centerImageWidth || 400, 350)),
                                                    height: appendPx(Math.min(data.centerImageHeight || 500, 400)),
                                                    objectFit: data.centerImageFit || 'cover',
                                                    borderRadius: appendPx(data.centerImageBorderRadius || 12),
                                                    boxShadow: data.centerImageShadow ? '0 8px 32px rgba(0, 0, 0, 0.15)' : 'none'
                                                }}
                                            />
                                        </div>
                                    )}
                                    
                                    {/* Mobile Features */}
                                    <div style={{
                                        width: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: `${data.featureVerticalSpacing || 60}px` // ✅ Dynamischer Gap auch für Mobile
                                    }}>
                                        {data.features?.map((feature, index) => {
                                            // ✅ CHECK INDIVIDUAL FEATURE VISIBILITY (Mobile - All Features)
                                            const isFeatureVisible = data[`showFeature${index + 1}`] !== false;
                                            
                                            if (!isFeatureVisible) return null;
                                            
                                            return (
                                            <div 
                                                key={`${feature.id}-${animationKey}`}
                                                className={data.enableAnimations && isEditMode ? `spiritual-feature-animated ${data.animationType || 'fadeInUp'}` : ''}
                                                style={{
                                                    textAlign: 'center',
                                                    maxWidth: '500px',
                                                    margin: '0 auto',
                                                    animationDuration: data.enableAnimations && isEditMode ? `${data.animationDuration || 600}ms` : '0s',
                                                    animationDelay: data.enableAnimations && isEditMode ? `${index * (data.animationDelay || 100)}ms` : '0s'
                                                }}
                                            >
                                                {/* ✅ MINI TITLE - oben über dem Haupttitel */}
                                                {feature.showMiniTitle !== false && feature.miniTitle && (
                                                    renderEditableText(
                                                        `${index}.miniTitle`, 
                                                        feature.miniTitle, 
                                                        'div', 
                                                        {
                                                            color: data.featureDescriptionColor || '#5a5a5a', // ✅ Übernimmt Description-Farbe
                                                            fontSize: appendPx(data.featureMiniTitleSize || 12),
                                                            fontWeight: data.featureMiniTitleWeight || '500',
                                                            fontFamily: data.featureMiniTitleFont || fonts.globalSmallFont,
                                                            lineHeight: '1.4',
                                                            textAlign: 'center',
                                                            margin: '0 0 6px 0',
                                                            textTransform: 'uppercase',
                                                            letterSpacing: '0.5px'
                                                        },
                                                        'Enter mini title...'
                                                    )
                                                )}

                                                {renderEditableText(
                                                    `${index}.title`, 
                                                    feature.title, 
                                                    'h3', 
                                                    {
                                                        color: data.featureTitleColor || '#2c3e50',
                                                        fontSize: appendPx(data.featureTitleSize || 24),
                                                        fontWeight: data.featureTitleWeight || '700',
                                                        fontFamily: data.featureTitleFont || fonts.globalBigFont,
                                                        lineHeight: '1.3',
                                                        textAlign: 'center',
                                                        margin: '0 0 12px 0'
                                                    },
                                                    'Enter feature title...'
                                                )}

                                                {/* ✅ TITLE SEPARATOR - dünne Linie unter dem Titel */}
                                                {data.showFeatureTitleSeparator && (
                                                    <div style={{
                                                        display: 'flex',
                                                        justifyContent: 'center',
                                                        marginBottom: appendPx(data.featureTitleSeparatorSpacing || 8)
                                                    }}>
                                                        <div style={{
                                                            width: `${data.featureTitleSeparatorWidth || 30}%`,
                                                            height: appendPx(data.featureTitleSeparatorThickness || 2),
                                                            backgroundColor: data.featureTitleSeparatorColor || data.featureTitleColor || '#5C80BC',
                                                            borderRadius: appendPx((data.featureTitleSeparatorThickness || 2) / 2)
                                                        }} />
                                                    </div>
                                                )}
                                                
                                                {renderEditableText(
                                                    `${index}.description`, 
                                                    feature.description, 
                                                    'p', 
                                                    {
                                                        color: data.featureDescriptionColor || '#5a5a5a',
                                                        fontSize: appendPx(data.featureDescriptionSize || 16),
                                                        fontWeight: data.featureDescriptionWeight || '400',
                                                        fontFamily: data.featureDescriptionFont || fonts.globalSmallFont,
                                                        lineHeight: '1.6',
                                                        textAlign: 'center',
                                                        margin: '0 0 12px 0'
                                                    },
                                                    'Enter feature description...'
                                                )}

                                                {/* ✅ PRICE LABEL - unten unter der Description, zentriert wie Text */}
                                                {feature.showPriceLabel !== false && feature.priceLabel && (
                                                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                                                        {renderEditableText(
                                                            `${index}.priceLabel`, 
                                                            feature.priceLabel, 
                                                            'div', 
                                                            {
                                                                color: data.featureTitleColor || '#2c3e50', // ✅ Übernimmt Titel-Farbe
                                                                fontSize: appendPx(data.featurePriceLabelSize || 14),
                                                                fontWeight: data.featurePriceLabelWeight || '600',
                                                                fontFamily: data.featurePriceLabelFont || fonts.globalSmallFont,
                                                                lineHeight: '1.4',
                                                                textAlign: 'center',
                                                                margin: '0',
                                                                display: 'inline-block'
                                                            },
                                                            'Enter price...'
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                            );
                                        })}
                                    </div>
                                </>
                            )}
                        </div>
                    )}

                    {/* IMAGE TOP VERTICAL LAYOUT */}
                    {(data.layoutType === 'image-top-vertical' || data.layoutStyle === 'image-top-vertical') && (
                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: `${data.featureVerticalSpacing || 60}px` // ✅ Dynamischer Gap für image-top-vertical
                        }}>
                            {/* Image oben */}
                            {data.showCenterImage && data.centerImage && (
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'center'
                                }}>
                                    <img
                                        src={data.centerImage}
                                        alt="Spiritual Feature"
                                        style={{
                                            width: appendPx(data.centerImageWidth || 400),
                                            height: appendPx(data.centerImageHeight || 400),
                                            objectFit: data.centerImageFit || 'cover',
                                            borderRadius: appendPx(data.centerImageBorderRadius || 12),
                                            boxShadow: data.centerImageShadow ? '0 8px 32px rgba(0, 0, 0, 0.15)' : 'none'
                                        }}
                                    />
                                </div>
                            )}
                            
                            {/* Features vertikal untereinander */}
                            <div style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: `${data.featureVerticalSpacing || 60}px`, // ✅ Dynamischer Gap zwischen Features
                                alignItems: 'center',
                                width: '100%'
                            }}>
                                {data.features?.map((feature, index) => (
                                    <div 
                                        key={`feature-${index}-${animationKey}`} // ✅ FIXED: Verwende index statt undefined feature.id
                                        className={data.enableAnimations && isEditMode ? `spiritual-feature-animated ${data.animationType || 'fadeInUp'}` : ''}
                                        style={{
                                            textAlign: 'center',
                                            maxWidth: '600px',
                                            animationDuration: data.enableAnimations && isEditMode ? `${data.animationDuration || 600}ms` : '0s',
                                            animationDelay: data.enableAnimations && isEditMode ? `${index * (data.animationDelay || 100)}ms` : '0s'
                                        }}
                                    >
                                        {/* ✅ MINI TITLE - oben über dem Haupttitel */}
                                        {feature.showMiniTitle !== false && feature.miniTitle && (
                                            renderEditableText(
                                                `${index}.miniTitle`, 
                                                feature.miniTitle, 
                                                'div', 
                                                {
                                                    color: data.featureDescriptionColor || '#5a5a5a', // ✅ Übernimmt Description-Farbe
                                                    fontSize: appendPx(data.featureMiniTitleSize || 12),
                                                    fontWeight: data.featureMiniTitleWeight || '500',
                                                    fontFamily: data.featureMiniTitleFont || fonts.globalSmallFont,
                                                    lineHeight: '1.4',
                                                    textAlign: 'center',
                                                    margin: '0 0 6px 0',
                                                    textTransform: 'uppercase',
                                                    letterSpacing: '0.5px'
                                                },
                                                'Enter mini title...'
                                            )
                                        )}

                                        {renderEditableText(
                                            `${index}.title`, 
                                            feature.title, 
                                            'h3', 
                                            {
                                                color: data.featureTitleColor || '#2c3e50',
                                                fontSize: appendPx(data.featureTitleSize || 28),
                                                fontWeight: data.featureTitleWeight || '700',
                                                fontFamily: data.featureTitleFont || fonts.globalBigFont,
                                                lineHeight: '1.3',
                                                textAlign: 'center',
                                                margin: '0 0 12px 0'
                                            },
                                            'Enter feature title...'
                                        )}

                                        {/* ✅ TITLE SEPARATOR - dünne Linie unter dem Titel */}
                                        {data.showFeatureTitleSeparator && (
                                            <div style={{
                                                display: 'flex',
                                                justifyContent: 'center',
                                                marginBottom: appendPx(data.featureTitleSeparatorSpacing || 8)
                                            }}>
                                                <div style={{
                                                    width: `${data.featureTitleSeparatorWidth || 30}%`,
                                                    height: appendPx(data.featureTitleSeparatorThickness || 2),
                                                    backgroundColor: data.featureTitleSeparatorColor || data.featureTitleColor || '#5C80BC',
                                                    borderRadius: appendPx((data.featureTitleSeparatorThickness || 2) / 2)
                                                }} />
                                            </div>
                                        )}
                                        
                                        {renderEditableText(
                                            `${index}.description`, 
                                            feature.description, 
                                            'p', 
                                            {
                                                color: data.featureDescriptionColor || '#5a5a5a',
                                                fontSize: appendPx(data.featureDescriptionSize || 16),
                                                fontWeight: data.featureDescriptionWeight || '400',
                                                fontFamily: data.featureDescriptionFont || fonts.globalSmallFont,
                                                lineHeight: '1.6',
                                                textAlign: 'center',
                                                margin: '0 0 12px 0',
                                                maxWidth: '600px'
                                            },
                                            'Enter feature description...'
                                        )}

                                        {/* ✅ PRICE LABEL - unten unter der Description, zentriert wie Text */}
                                        {feature.showPriceLabel !== false && feature.priceLabel && (
                                            <div style={{ display: 'flex', justifyContent: 'center' }}>
                                                {renderEditableText(
                                                    `${index}.priceLabel`, 
                                                    feature.priceLabel, 
                                                    'div', 
                                                    {
                                                        color: data.featureTitleColor || '#2c3e50', // ✅ Übernimmt Titel-Farbe
                                                        fontSize: appendPx(data.featurePriceLabelSize || 14),
                                                        fontWeight: data.featurePriceLabelWeight || '600',
                                                        fontFamily: data.featurePriceLabelFont || fonts.globalSmallFont,
                                                        lineHeight: '1.4',
                                                        textAlign: 'center',
                                                        margin: '0',
                                                        display: 'inline-block'
                                                    },
                                                    'Enter price...'
                                                )}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* IMAGE TOP HORIZONTAL LAYOUT */}
                    {(data.layoutType === 'image-top-horizontal' || data.layoutStyle === 'image-top-horizontal') && (
                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: `${data.featureVerticalSpacing || 60}px` // ✅ Dynamischer Gap für image-top-horizontal
                        }}>
                            {/* Image oben */}
                            {data.showCenterImage && data.centerImage && (
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'center'
                                }}>
                                    <img
                                        src={data.centerImage}
                                        alt="Spiritual Feature"
                                        style={{
                                            width: appendPx(data.centerImageWidth || 400),
                                            height: appendPx(data.centerImageHeight || 400),
                                            objectFit: data.centerImageFit || 'cover',
                                            borderRadius: appendPx(data.centerImageBorderRadius || 12),
                                            boxShadow: data.centerImageShadow ? '0 8px 32px rgba(0, 0, 0, 0.15)' : 'none'
                                        }}
                                    />
                                </div>
                            )}
                            
                            {/* Features horizontal in 2x2 Grid */}
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: isMobileLayout ? '1fr' : 'repeat(2, 1fr)',
                                gap: `${data.featureVerticalSpacing || 60}px`, // ✅ Dynamischer Gap im 2x2 Grid
                                width: '100%',
                                maxWidth: '800px'
                            }}>
                                {data.features?.map((feature, index) => (
                                    <div 
                                        key={`${feature.id}-${animationKey}`}
                                        className={data.enableAnimations && isEditMode ? `spiritual-feature-animated ${data.animationType || 'fadeInUp'}` : ''}
                                        style={{
                                            textAlign: 'center',
                                            animationDuration: data.enableAnimations && isEditMode ? `${data.animationDuration || 600}ms` : '0s',
                                            animationDelay: data.enableAnimations && isEditMode ? `${index * (data.animationDelay || 100)}ms` : '0s'
                                        }}
                                    >
                                        {/* ✅ MINI TITLE - oben über dem Haupttitel */}
                                        {feature.showMiniTitle !== false && feature.miniTitle && (
                                            renderEditableText(
                                                `${index}.miniTitle`, 
                                                feature.miniTitle, 
                                                'div', 
                                                {
                                                    color: data.featureDescriptionColor || '#5a5a5a', // ✅ Übernimmt Description-Farbe
                                                    fontSize: appendPx(data.featureMiniTitleSize || 11),
                                                    fontWeight: data.featureMiniTitleWeight || '500',
                                                    fontFamily: data.featureMiniTitleFont || fonts.globalSmallFont,
                                                    lineHeight: '1.4',
                                                    textAlign: 'center',
                                                    margin: '0 0 6px 0',
                                                    textTransform: 'uppercase',
                                                    letterSpacing: '0.5px'
                                                },
                                                'Enter mini title...'
                                            )
                                        )}

                                        {renderEditableText(
                                            `${index}.title`, 
                                            feature.title, 
                                            'h3', 
                                            {
                                                color: data.featureTitleColor || '#2c3e50',
                                                fontSize: appendPx(data.featureTitleSize || 24),
                                                fontWeight: data.featureTitleWeight || '700',
                                                fontFamily: data.featureTitleFont || fonts.globalBigFont,
                                                lineHeight: '1.3',
                                                textAlign: 'center',
                                                margin: '0 0 12px 0'
                                            },
                                            'Enter feature title...'
                                        )}

                                        {/* ✅ TITLE SEPARATOR - dünne Linie unter dem Titel */}
                                        {data.showFeatureTitleSeparator && (
                                            <div style={{
                                                display: 'flex',
                                                justifyContent: 'center',
                                                marginBottom: appendPx(data.featureTitleSeparatorSpacing || 8)
                                            }}>
                                                <div style={{
                                                    width: `${data.featureTitleSeparatorWidth || 30}%`,
                                                    height: appendPx(data.featureTitleSeparatorThickness || 2),
                                                    backgroundColor: data.featureTitleSeparatorColor || data.featureTitleColor || '#5C80BC',
                                                    borderRadius: appendPx((data.featureTitleSeparatorThickness || 2) / 2)
                                                }} />
                                            </div>
                                        )}
                                        
                                        {renderEditableText(
                                            `${index}.description`, 
                                            feature.description, 
                                            'p', 
                                            {
                                                color: data.featureDescriptionColor || '#5a5a5a',
                                                fontSize: appendPx(data.featureDescriptionSize || 15),
                                                fontWeight: data.featureDescriptionWeight || '400',
                                                fontFamily: data.featureDescriptionFont || fonts.globalSmallFont,
                                                lineHeight: '1.6',
                                                textAlign: 'center',
                                                margin: '0 0 12px 0'
                                            },
                                            'Enter feature description...'
                                        )}

                                        {/* ✅ PRICE LABEL - unten unter der Description, zentriert wie Text */}
                                        {feature.showPriceLabel !== false && feature.priceLabel && (
                                            <div style={{ display: 'flex', justifyContent: 'center' }}>
                                                {renderEditableText(
                                                    `${index}.priceLabel`, 
                                                    feature.priceLabel, 
                                                    'div', 
                                                    {
                                                        color: data.featureTitleColor || '#2c3e50', // ✅ Übernimmt Titel-Farbe
                                                        fontSize: appendPx(data.featurePriceLabelSize || 13),
                                                        fontWeight: data.featurePriceLabelWeight || '600',
                                                        fontFamily: data.featurePriceLabelFont || fonts.globalSmallFont,
                                                        lineHeight: '1.4',
                                                        textAlign: 'center',
                                                        margin: '0',
                                                        display: 'inline-block'
                                                    },
                                                    'Enter price...'
                                                )}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                </div>
            </div>
        </>
    );
}

export default SpiritualFeatureBanner;
